import { TENANT_API_BASE_URL } from "../env";

/**
 * A tiny, deliberately separate fetch wrapper for exercising a REAL impersonation session
 * token (`ImpersonationSession.access_token`, a genuine `aud: "tenant"` JWT once issued by
 * `realPlatformApiClient.impersonateTenant`) against the tenant-realm API it's actually good
 * for — `/api/v1/auth/me`, `/api/v1/branches` — never against `/api/v1/platform/*`.
 *
 * This is intentionally NOT part of `PlatformApiClient` — that interface models the
 * platform-operator realm only (`aud: "platform"`). An impersonation token is the opposite:
 * a real tenant-realm token minted BY a platform operator. Folding it into the platform
 * client would misrepresent which realm's auth it actually carries.
 *
 * Used by `pages/tenants/ImpersonationViewPage.tsx` to prove — visibly, to the operator —
 * that a "view as tenant" session is a genuine, working tenant login, not just an opaque
 * token nobody ever exercises.
 */

export interface ImpersonatedMe {
  id: string;
  tenant_id: string;
  email: string;
  role: string;
  assigned_branch_id: string | null;
}

/** Mirrors `backend/app/schemas/tenant.py::BranchOut` field-for-field (`location_address`,
 * not `address` — read directly rather than assumed). */
export interface ImpersonatedBranch {
  id: string;
  name: string;
  location_address: string | null;
  is_active: boolean;
}

async function tenantRealmGet<T>(path: string, accessToken: string): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${TENANT_API_BASE_URL}${path}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    throw new Error(`Could not reach the backend: ${message}`);
  }
  if (!response.ok) {
    let detail = `Request failed with status ${response.status}`;
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body.detail === "string") detail = body.detail;
    } catch {
      // Non-JSON error body — fall through to the generic message.
    }
    throw new Error(detail);
  }
  return (await response.json()) as T;
}

/** `GET /api/v1/auth/me` — the simplest possible proof the impersonation token actually
 * authenticates as the impersonated tenant user (`backend/app/schemas/auth.py::UserOut`). */
export function fetchImpersonatedMe(accessToken: string): Promise<ImpersonatedMe> {
  return tenantRealmGet<ImpersonatedMe>("/auth/me", accessToken);
}

/** `GET /api/v1/branches` — any authenticated tenant-realm role can read this; gives the
 * operator something concrete to look at beyond a bare identity check. */
export function fetchImpersonatedBranches(accessToken: string): Promise<ImpersonatedBranch[]> {
  return tenantRealmGet<ImpersonatedBranch[]>("/branches", accessToken);
}
